import React from 'react';
import { cn } from 'common/utils';
import { useMounted } from 'common/utils/hooks/useDidMount';
import OverlaySkeleton from 'components/v2/atoms/overlay-skeleton';
import Breadcrumb from 'components/v2/molecules/breadcrumb';
import ShareSection from 'components/v2/molecules/share-section';
import { ArticleWithContentType } from 'service/article';

import styles from './article-interview.module.scss';

export interface InterviewQuestionType {
  id: string;
  questionNumber: string;
  questionText: string;
  answerText: string;
}

export interface CreatorProfileType {
  name: string;
  title: string;
  avatar: string;
  profileUrl?: string;
  avatarPortraitHeight?: number;
}

export interface InterviewArticleType extends ArticleWithContentType {
  heroSubtitle?: string;
  interviewLabel?: string;
  leadText?: string;
  pullQuote?: string;
  pullQuoteIndex: number;
  questions: InterviewQuestionType[];
  creatorProfile: CreatorProfileType;
  isHidingCreatorNameAboveImage?: boolean;
  keyInsights?: {
    title: string;
    items: {
      icon: string;
      title: string;
      description: string;
    }[];
  };
  ctaSection?: {
    title: string;
    description: string;
    buttonText: string;
    buttonUrl: string;
  };
}

const ArticleInterview = ({ article }: { article: InterviewArticleType }) => {
  const {
    breadcrumbItems,
    date,
    author,
    readTime,
    metadata,
    heroSubtitle,
    interviewLabel,
    leadText,
    pullQuote,
    pullQuoteIndex,
    questions,
    creatorProfile,
    keyInsights,
    ctaSection,
    isHidingCreatorNameAboveImage,
  } = article;

  const isMounted = useMounted();

  return (
    <div className={cn('', styles['interview-container'])}>
      {/* Breadcrumb */}
      <div className="mx-auto max-w-screen-xl px-5 pt-5 md:px-10 md:pt-10">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Hero Section */}
      <section className={cn('', styles['hero-section'])}>
        <div className={cn('', styles['hero-container'])}>
          <div className={cn('', styles['hero-text'])}>
            {interviewLabel && (
              <span className={cn('', styles['interview-label'])}>
                {interviewLabel}
              </span>
            )}
            <h1 className={cn('', styles['hero-title'])}>{metadata.title}</h1>
            {heroSubtitle && (
              <p className={cn('', styles['hero-subtitle'])}>{heroSubtitle}</p>
            )}

            <div className={cn('', styles['article-info'])}>
              <div className={cn('', styles['author-info'])}>
                <div className={cn('', styles['author-avatar-small'])}>
                  <img src={author.avatar} alt={author.name} />
                </div>
                <span>聞き手：{author.name}</span>
              </div>
              <span>{date}</span>
              <span>{readTime}で読める</span>
            </div>
          </div>

          <div className={cn('', styles['hero-image'])}>
            <div
              className={cn('', styles['creator-portrait'])}
              style={
                creatorProfile.avatarPortraitHeight && {
                  height: `${creatorProfile.avatarPortraitHeight}px`,
                }
              }
            >
              <img src={creatorProfile.avatar} alt={creatorProfile.name} />
              {!isHidingCreatorNameAboveImage && (
                <div className={cn('', styles['creator-name-badge'])}>
                  <div className={cn('', styles['creator-name'])}>
                    {creatorProfile.name}
                  </div>
                  <div className={cn('', styles['creator-title'])}>
                    {creatorProfile.title}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Lead Text */}
      {leadText && (
        <section className={cn('', styles['introduction-section'])}>
          {isMounted ? (
            <p
              className={cn('', styles['lead-text'])}
              dangerouslySetInnerHTML={{ __html: leadText }}
            />
          ) : (
            <div className="relative">
              <OverlaySkeleton className="bg-white" />
              <p className={cn('', styles['lead-text'])}>{leadText}</p>
            </div>
          )}
        </section>
      )}

      {/* Q&A Section */}
      <section className={cn('', styles['qa-section'])}>
        {questions.map((question, index) => (
          <React.Fragment key={question.id}>
            <div
              className={cn(
                '',
                styles['qa-item'],
                index % 2 === 1 ? styles['reverse'] : '',
              )}
            >
              <div className={cn('', styles['question-block'])}>
                <div className={cn('', styles['question-number'])}>
                  {question.questionNumber.padStart(2, '0')}
                </div>
                <h2 className={cn('', styles['question-text'])}>
                  {question.questionText}
                </h2>
              </div>
              <div className={cn('', styles['answer-block'])}>
                {isMounted ? (
                  <div
                    className={cn('', styles['answer-text'])}
                    dangerouslySetInnerHTML={{ __html: question.answerText }}
                  />
                ) : (
                  <div className="relative">
                    <OverlaySkeleton className="bg-white" />
                    <div className={cn('', styles['answer-text'])}>
                      {question.answerText}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Pull Quote after question 3 */}
            {index === pullQuoteIndex && pullQuote && (
              <div className={cn('', styles['pull-quote'])}>
                <div className={cn('', styles['pull-quote-mark'])}>"</div>
                {isMounted ? (
                  <p
                    className={cn('', styles['pull-quote-text'])}
                    dangerouslySetInnerHTML={{ __html: pullQuote }}
                  />
                ) : (
                  <div className="relative">
                    <OverlaySkeleton className="bg-white" />
                    <p className={cn('', styles['pull-quote-text'])}>
                      {pullQuote}
                    </p>
                  </div>
                )}
              </div>
            )}
          </React.Fragment>
        ))}
      </section>

      {/* Key Insights */}
      {keyInsights && (
        <section className={cn('', styles['insights-section'])}>
          <div className={cn('', styles['insights-container'])}>
            <h2 className={cn('', styles['insights-title'])}>
              {keyInsights.title}
            </h2>
            <div className={cn('', styles['insights-grid'])}>
              {keyInsights.items.map((insight, index) => (
                <div key={index} className={cn('', styles['insight-card'])}>
                  <div className={cn('', styles['insight-icon'])}>
                    {insight.icon}
                  </div>
                  <h3 className={cn('', styles['insight-title'])}>
                    {insight.title}
                  </h3>
                  <p className={cn('', styles['insight-text'])}>
                    {insight.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      {ctaSection && (
        <section className={cn('', styles['cta-section'])}>
          <div className="mx-auto max-w-[800px] px-5 md:px-10">
            <h2 className={cn('', styles['cta-title'])}>{ctaSection.title}</h2>
            <p className={cn('', styles['cta-description'])}>
              {ctaSection.description}
            </p>
            <a
              href={ctaSection.buttonUrl}
              className={cn('', styles['cta-primary'])}
              target="_blank"
              rel="noopener noreferrer"
            >
              {ctaSection.buttonText}
            </a>
          </div>
        </section>
      )}

      {/* Action Section */}
      {/* <div className="mx-auto max-w-[1000px] px-5 md:px-10">
        <ActionSection />
      </div> */}

      {/* Share Section */}
      <div className="mx-auto max-w-[1000px] px-5 pb-10 md:px-10 md:pb-20">
        <ShareSection url={metadata.url} text={`${metadata.title}`} />
      </div>
    </div>
  );
};

export default ArticleInterview;
